import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { FiCopy, FiUserX } from 'react-icons/fi';

import BASE_URL, { IMAGE_DOMAIN } from '../../../../config/config';
import Loader from '../../../../components/Loader';
import Popup from '../../../../components/Popup';
import CropperModal from '../../../../components/ui/CropperModal';
import useCropper from '../../../../components/ui/useCropper';

import useDetalleUsuario from './useDetalleUsuario';
import HeroUsuario from './HeroUsuario';
import TarjetaTickets from './TarjetaTickets';
import TarjetaAuditoria from './TarjetaAuditoria';
import TarjetaServicios from './TarjetaServicios';
import Pestanas from './Pestanas';
import BarraGuardado from './BarraGuardado';
import FormularioInformacion from './FormularioInformacion';
import FormularioAjustes from './FormularioAjustes';

const CAMPOS_DE_AJUSTES = ['role', 'estado', 'dpto_tecnico'];

const textoContrasena = (usuarioData, contrasena) =>
  `Hola ${usuarioData.nombre},\n\nHemos restablecido tu contraseña de acceso a nuestro soporte técnico.\n\nUsuario: ${usuarioData.usuario}\nNueva contraseña: ${contrasena}\n\nPuedes acceder desde:\n👉 https://soporte.agenciaadhoc.com/iniciar-sesion\n\nPor motivos de seguridad, te recomendamos cambiarla en tu próximo inicio de sesión.\n\nAtentamente,\nEquipo de Soporte`;

const DetalleUsuario = () => {
  const { usuario } = useParams();
  const navigate = useNavigate();

  const {
    token, viewerRole, esAdmin,
    usuarioData, setUsuarioData, form, actualizarCampo, cargando, error,
    tickets, cargandoTickets,
    auditoria, cargandoAuditoria,
    catalogoServicios, serviciosUsuario, serviciosAsignados, alternarServicio, cargandoServicios,
    camposCambiados, serviciosCambiados, totalCambios, hayCambios,
    descartarCambios, guardar, cambiarEstado,
  } = useDetalleUsuario(usuario);

  const [tabActiva, setTabActiva] = useState('informacion');
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const [confirmacion, setConfirmacion] = useState(null);
  const [salidaPendiente, setSalidaPendiente] = useState(null);

  const [reseteando, setReseteando] = useState(false);
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [resetTempPassword, setResetTempPassword] = useState('');

  const [fotoPreview, setFotoPreview] = useState(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fileInputRef = useRef(null);

  const { rawImageSrc, showCropper, crop, setCrop, zoom, setZoom,
    onCropComplete, openCropper, cancelCrop, applyCrop } = useCropper({
    onCropDone: async ({ blob, previewUrl }) => {
      setSubiendoFoto(true);
      try {
        const fd = new FormData();
        fd.append('fotoPerfil', blob, 'avatar.webp');
        const resp = await axios.put(`${BASE_URL}/usuario/actualizar-imagen/${usuario}`, fd, {
          headers: { Authorization: token, 'Content-Type': 'multipart/form-data' },
        });
        if (resp.data.success) {
          setFotoPreview(previewUrl);
          toast.success('Foto de perfil actualizada correctamente.');
        } else {
          toast.error(resp.data.message || 'Error al actualizar la imagen.');
        }
      } catch (err) {
        console.error('Error al actualizar la imagen:', err);
        toast.error('Error al actualizar la imagen.');
      } finally {
        setSubiendoFoto(false);
      }
    },
  });

  // Aviso del navegador al cerrar la pestaña con cambios sin guardar
  useEffect(() => {
    if (!hayCambios) return undefined;
    const alSalir = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);

  const copiar = async (texto, mensaje) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(mensaje);
    } catch (err) {
      toast.error('No se pudo copiar.');
    }
  };

  const validarFormulario = () => {
    const errs = {};

    if (!form.nombre || form.nombre.trim() === '') errs.nombre = 'El nombre no puede estar vacío.';
    if (!form.apellidos || form.apellidos.trim() === '') errs.apellidos = 'Los apellidos no pueden estar vacíos.';
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Email inválido.';

    if (form.telefono && form.telefono.trim() !== '') {
      try {
        if (!isValidPhoneNumber(form.telefono, 'ES')) errs.telefono = 'Teléfono inválido.';
      } catch (e) {
        errs.telefono = 'Formato de teléfono no reconocido.';
      }
    }

    return errs;
  };

  const ejecutarGuardado = async () => {
    setGuardando(true);
    try {
      await guardar();
      toast.success('Datos actualizados correctamente.');
    } catch (err) {
      console.error('Error al actualizar:', err);
      toast.error(err.response?.data?.message || 'Hubo un error al guardar los cambios.');
    } finally {
      setGuardando(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (guardando) return;

    const errs = validarFormulario();
    if (Object.keys(errs).length > 0) {
      setErrores(errs);
      // Los errores viven en la pestaña de información: llevamos al usuario allí
      setTabActiva('informacion');
      toast.error('Revisa los campos marcados antes de guardar.');
      return;
    }
    setErrores({});

    const pasaAAdmin = form.role === 'admin' && usuarioData.role !== 'admin';
    const pasaASuspendido = form.estado === 'suspendido' && usuarioData.estado !== 'suspendido';

    if (pasaAAdmin || pasaASuspendido) {
      const mensajes = [];
      if (pasaAAdmin) mensajes.push('Vas a darle permisos de administrador a este usuario.');
      if (pasaASuspendido) mensajes.push('Vas a suspender el acceso de este usuario.');
      setConfirmacion({
        titulo: 'Confirmar cambio',
        mensaje: mensajes.join(' '),
        onConfirmar: ejecutarGuardado,
      });
      return;
    }

    await ejecutarGuardado();
  };

  const aplicarCambioEstado = async (nuevoEstado) => {
    setCambiandoEstado(true);
    try {
      await cambiarEstado(nuevoEstado);
      toast.success(nuevoEstado === 'suspendido' ? 'Cuenta suspendida.' : 'Cuenta verificada.');
    } catch (err) {
      console.error('Error al cambiar el estado:', err);
      toast.error('No se pudo cambiar el estado de la cuenta.');
    } finally {
      setCambiandoEstado(false);
    }
  };

  const pedirCambioEstado = (nuevoEstado) => {
    if (nuevoEstado === 'suspendido') {
      setConfirmacion({
        titulo: 'Suspender cuenta',
        mensaje: 'El usuario perderá el acceso al soporte hasta que reactives su cuenta.',
        onConfirmar: () => aplicarCambioEstado('suspendido'),
      });
      return;
    }
    aplicarCambioEstado(nuevoEstado);
  };

  // Cualquier salida (volver, cambiar de pestaña) pasa por aquí para no perder
  // cambios sin querer.
  const intentarSalir = (accion) => {
    if (!hayCambios) {
      accion();
      return;
    }
    setSalidaPendiente(() => accion);
  };

  const resetearContrasena = async () => {
    setReseteando(true);
    try {
      const resp = await axios.put(`${BASE_URL}/usuario/reset-password/${usuario}`, {}, {
        headers: { Authorization: token },
      });
      if (resp.data.success) {
        setResetTempPassword(resp.data.tempPassword);
        setShowResetPopup(true);
      } else {
        toast.error(resp.data.message || 'No se pudo resetear la contraseña.');
      }
    } catch (err) {
      console.error('Error al resetear la contraseña:', err);
      toast.error('No se pudo resetear la contraseña.');
    } finally {
      setReseteando(false);
    }
  };

  const irAVerTodosLosTickets = () => {
    try {
      const filtrosActuales = JSON.parse(localStorage.getItem('ticketManagerFiltros') || '{}');
      localStorage.setItem('ticketManagerFiltros', JSON.stringify({ ...filtrosActuales, searchText: usuario }));
    } catch (err) {
      // ignore
    }
    navigate('/dashboard/tickets');
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) openCropper(file);
    e.target.value = '';
  };

  // Los chips laterales muestran la selección pendiente del admin, no la
  // guardada: así el lateral y el asignador cuentan lo mismo.
  const serviciosVisibles = useMemo(() => {
    if (!esAdmin) return serviciosUsuario;
    const porId = new Map([...serviciosUsuario, ...catalogoServicios].map((s) => [s.id, s]));
    return serviciosAsignados.map((id) => porId.get(id)).filter(Boolean);
  }, [esAdmin, serviciosUsuario, catalogoServicios, serviciosAsignados]);

  const pestanas = useMemo(() => {
    const cambiosAjustes = camposCambiados.filter((c) => CAMPOS_DE_AJUSTES.includes(c)).length
      + (serviciosCambiados ? 1 : 0);
    const lista = [
      { id: 'informacion', label: 'Información', pendientes: camposCambiados.length - (camposCambiados.filter((c) => CAMPOS_DE_AJUSTES.includes(c)).length) },
    ];
    if (esAdmin) lista.push({ id: 'ajustes', label: 'Ajustes avanzados', pendientes: cambiosAjustes });
    return lista;
  }, [camposCambiados, serviciosCambiados, esAdmin]);

  if (cargando) return <div className="loader-fullscreen"><Loader /></div>;

  if (error || !usuarioData) {
    return (
      <main className="site-width perfil-usuario-page">
        <div className="perfil-vacio perfil-vacio--pagina">
          <FiUserX />
          <p>{error || 'Usuario no encontrado.'}</p>
          <button className="perfil-btn-primary" onClick={() => navigate('/dashboard/usuarios')}>
            Volver al listado
          </button>
        </div>
      </main>
    );
  }

  const fotoActual = fotoPreview
    || (usuarioData.fotoPerfil
      ? (usuarioData.fotoPerfil.startsWith('http') ? usuarioData.fotoPerfil : `${IMAGE_DOMAIN}${usuarioData.fotoPerfil}`)
      : `${IMAGE_DOMAIN}/images/default-user.png`);

  return (
    <main className="site-width perfil-usuario-page">
      <ToastContainer position="bottom-right" autoClose={3000} />

      <button className="boton-volver" onClick={() => intentarSalir(() => navigate('/dashboard/usuarios'))}>
        ← Volver
      </button>

      <div className="pagina-cabecera">
        <h2>Ficha del usuario</h2>
        <p>Consulta y edita los datos, los permisos y los servicios contratados de este usuario.</p>
      </div>

      <HeroUsuario
        usuarioData={usuarioData}
        fotoActual={fotoActual}
        subiendoFoto={subiendoFoto}
        onCambiarFoto={() => fileInputRef.current?.click()}
        puedeCambiarEstado={esAdmin}
        cambiandoEstado={cambiandoEstado}
        onCambiarEstado={pedirCambioEstado}
        onCopiar={copiar}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {showCropper && (
        <CropperModal
          rawImageSrc={rawImageSrc}
          crop={crop} setCrop={setCrop}
          zoom={zoom} setZoom={setZoom}
          onCropComplete={onCropComplete}
          onCancel={cancelCrop}
          onApply={applyCrop}
        />
      )}

      <div className="perfil-body">
        <aside className="perfil-sidebar">
          <TarjetaServicios
            servicios={serviciosVisibles}
            cargando={cargandoServicios}
            editable={esAdmin}
            onQuitar={alternarServicio}
            hayPendientes={serviciosCambiados}
          />

          <TarjetaTickets
            tickets={tickets}
            cargando={cargandoTickets}
            onVerTodos={() => intentarSalir(irAVerTodosLosTickets)}
          />

          {esAdmin && (
            <TarjetaAuditoria auditoria={auditoria} cargando={cargandoAuditoria} />
          )}
        </aside>

        <section className="perfil-main">
          <Pestanas
            pestanas={pestanas}
            activa={tabActiva}
            onCambiar={setTabActiva}
          />

          <form className="perfil-form" onSubmit={handleSubmit}>
            <div
              role="tabpanel"
              id={`panel-${tabActiva}`}
              aria-labelledby={`tab-${tabActiva}`}
            >
              {tabActiva === 'informacion' && (
                <FormularioInformacion
                  usuarioData={usuarioData}
                  form={form}
                  errores={errores}
                  onCampo={actualizarCampo}
                />
              )}

              {tabActiva === 'ajustes' && esAdmin && (
                <FormularioAjustes
                  form={form}
                  onCampo={actualizarCampo}
                  catalogoServicios={catalogoServicios}
                  serviciosAsignados={serviciosAsignados}
                  onAlternarServicio={alternarServicio}
                  cargandoServicios={cargandoServicios}
                  reseteando={reseteando}
                  onResetearContrasena={resetearContrasena}
                />
              )}
            </div>

            <BarraGuardado
              visible={hayCambios}
              totalCambios={totalCambios}
              guardando={guardando}
              onDescartar={() => { descartarCambios(); setErrores({}); }}
            />
          </form>
        </section>
      </div>

      {/* Confirmación de acciones sensibles (rol admin, suspensión) */}
      <Popup
        open={Boolean(confirmacion)}
        title={confirmacion?.titulo || 'Confirmar cambio'}
        onClose={() => setConfirmacion(null)}
        showConfirm={false}
        footerActions={(
          <button
            className="boton-aceptar-popup"
            onClick={() => {
              const accion = confirmacion?.onConfirmar;
              setConfirmacion(null);
              if (accion) accion();
            }}
          >
            Confirmar
          </button>
        )}
      >
        <p>{confirmacion?.mensaje}</p>
        <p>¿Quieres continuar?</p>
      </Popup>

      {/* Salir con cambios sin guardar */}
      <Popup
        open={Boolean(salidaPendiente)}
        title="Tienes cambios sin guardar"
        onClose={() => setSalidaPendiente(null)}
        showConfirm={false}
        footerActions={(
          <button
            className="boton-aceptar-popup"
            onClick={() => {
              const accion = salidaPendiente;
              setSalidaPendiente(null);
              descartarCambios();
              if (accion) accion();
            }}
          >
            Salir sin guardar
          </button>
        )}
      >
        <p>Si sales ahora se perderán {totalCambios === 1 ? 'el cambio' : `los ${totalCambios} cambios`} que has hecho.</p>
      </Popup>

      {/* Resultado del reseteo de contraseña */}
      <Popup
        open={showResetPopup}
        title="Contraseña reseteada"
        onClose={() => setShowResetPopup(false)}
        showConfirm={false}
        footerActions={(
          <button
            className="boton-aceptar-popup"
            onClick={() => copiar(textoContrasena(usuarioData, resetTempPassword), 'Texto copiado al portapapeles.')}
          >
            Copiar texto completo
          </button>
        )}
      >
        <div className="form-crear-usuario">
          <div className="form-field">
            <label>Nueva contraseña temporal</label>
            <div className="reset-password-fila">
              <p className="reset-password-valor">{resetTempPassword}</p>
              <button
                type="button"
                className="perfil-btn-secundario"
                onClick={() => copiar(resetTempPassword, 'Contraseña copiada.')}
              >
                <FiCopy /> Copiar
              </button>
            </div>
          </div>
          <div className="form-field">
            <label>Texto para enviar (email / WhatsApp)</label>
            <textarea readOnly rows={8} value={textoContrasena(usuarioData, resetTempPassword)} />
          </div>
        </div>
      </Popup>
    </main>
  );
};

export default DetalleUsuario;
